import { createContext, useEffect, useState } from "react";
import { PanelSettings } from "@/types";
import { IPanelScope, ISearchResult, ITreeItem } from "@/interfaces";
import {
  CacheService,
  FiltersService,
  TreeService,
  SearchService,
} from "@/services";
import {
  DEFAULT_FILTER_KEY,
  GENERIC_ERROR_MESSAGE,
  MIN_CRITERIA_LENGTH,
} from "@/constants";
import { redirect } from "@/utils";
import { findNodeByIdAsync, createTreeFetchCallback } from "@/utils/common";

export interface IFilterStore {
  items: ITreeItem[];
  loading: boolean;
  error: string | null;
  settings: PanelSettings;
  scope: IPanelScope;
  criteria: string;
  selectedItem: ITreeItem | null;
  setItems: (value: ITreeItem[]) => void;
  addSubFilter: (id: string) => void;
  deleteFilter: (id: string) => void;
  editView?: (id: string) => void;
  createView?: (
    parentId: string | null,
    filterType?: "manual" | "auto",
  ) => void;
  copyAsPtql?: (filterId: string, autoFilterValues?: string[]) => void;
  moveView?: (id: string) => void;
  getDeletionPreventionStatus: (id: string) => Promise<any>;
  fetchFilters: () => void;
  reloadItems: (newState: ITreeItem[]) => void;
  setCriteria: (value: string) => void;
  setSelectedItem: (value: ITreeItem | null) => void;
  notifyFilterChanged: (node: any, isUserAction: boolean) => void;
  handleNodeExpand: (node: ITreeItem) => void;
  handleServerSideSearch: (criteria: string) => void;
  setLoading: (value: boolean) => void;
}

interface IFilterProvider {
  children: React.ReactNode;
  scope: IPanelScope;
  settings: PanelSettings;
  newFilterId?: string;
  notifyFilterChanged: (node: ITreeItem | null, isUserAction: boolean) => void;
  editView?: (id: string) => void;
  createView?: (
    parentId: string | null,
    filterType?: "manual" | "auto",
  ) => void;
  copyAsPtql?: (filterId: string, autoFilterValues?: string[]) => void;
  moveView?: (id: string) => void;
}

export const FiltersContext = createContext<IFilterStore | undefined>(
  undefined,
);

export const FilterProvider = ({
  children,
  scope,
  settings,
  newFilterId,
  notifyFilterChanged,
  editView,
  createView,
  copyAsPtql,
  moveView,
}: IFilterProvider) => {
  const [items, setItems] = useState<ITreeItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [criteria, setCriteria] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<ITreeItem | null>(null);

  useEffect(() => {
    if (criteria.length < MIN_CRITERIA_LENGTH) {
      setError(null);
    }
  }, [criteria]);

  useEffect(() => {
    (async () => {
      if (scope) {
        const filters = await fetchFilters();
        const urlFilterId =
          FiltersService.getActiveFilterIdFromUrl() || DEFAULT_FILTER_KEY;
        if (filters?.length && urlFilterId !== DEFAULT_FILTER_KEY) {
          const fetchCallback = createTreeFetchCallback(scope, () =>
            setItems([...filters]),
          );
          const activeUrlFilter = await findNodeByIdAsync(
            filters,
            urlFilterId,
            fetchCallback,
          );
          if (activeUrlFilter?.ancestry) {
            const ancestorIds = activeUrlFilter.ancestry.split("/");
            const openNodes = TreeService.getOpenedNodes(scope);
            const newNodes = ancestorIds.filter(
              (id: string) => !openNodes.includes(id),
            );
            if (newNodes.length) {
              TreeService.setOpenedNodes(scope, [...openNodes, ...newNodes]);
              setItems([...filters]);
            }
          }
          notifyFilterChanged(activeUrlFilter || null, false);
          if (activeUrlFilter) {
            setTimeout(() => TreeService.scrollToSelected(), 1000);
          }
        } else {
          // Without bookmarks (e.g. in a modal) the url has no filter param, but the
          // tree still highlights the stored active filter - load its entities too
          const activeNode = settings?.disableBookmarks
            ? TreeService.getActiveNode(scope)
            : null;
          const node =
            activeNode?.id && activeNode.id !== DEFAULT_FILTER_KEY
              ? SearchService.findFilterItem(filters || [], activeNode.id) ||
                activeNode
              : null;
          notifyFilterChanged(node, false);
        }
      }
    })();
  }, [`${scope.module}${scope.projectId}${scope.userId}`, newFilterId]);

  useEffect(() => {
    if (items.length) {
      const urlFilterId = FiltersService.getActiveFilterIdFromUrl();
      if (urlFilterId) {
        const activeUrlFilter = SearchService.findFilterItem(
          items,
          urlFilterId,
        );
        setSelectedItem(activeUrlFilter);
      } else {
        const activeNode = TreeService.getActiveNode(scope);
        setSelectedItem(activeNode);
      }
    }
  }, [items]);

  const reloadItems = async (newState: ITreeItem[]) => {
    setItems([...newState]);
  };

  const fetchFilters = async () => {
    let filters;
    try {
      setLoading(true);
      const response = await FiltersService.fetchFiltersFromServer(scope);

      filters = response.filters;
      setItems(filters);
    } catch (err) {
      console.error("fetchFilters->Failure fetch filters", err);
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }

    return filters;
  };

  useEffect(() => {
    const refreshEvents = ["pt:global-field-renamed", "pt:entities-updated"];
    const refresh = () => {
      CacheService.clearInMemoryCacheByPrefix();
      fetchFilters();
    };
    const storageHandler = (e: StorageEvent) => {
      if (e.key && refreshEvents.includes(e.key)) refresh();
    };
    refreshEvents.forEach((name) => window.addEventListener(name, refresh));
    window.addEventListener("storage", storageHandler);
    return () => {
      refreshEvents.forEach((name) =>
        window.removeEventListener(name, refresh),
      );
      window.removeEventListener("storage", storageHandler);
    };
  }, []);

  const deleteFilter = async (id: string) => {
    await FiltersService.deleteFilter(scope, id);
    notifyFilterChanged(null, false);
    TreeService.setActiveNode(scope, null);
    setSelectedItem(null);
  };

  const getDeletionPreventionStatus = async (id: string) => {
    return await FiltersService.getDeletionPreventionStatus(scope, id);
  };

  const addSubFilter = async (parentId: string) => {
    TreeService.expandTreeNode(scope, parentId);
    redirect(
      `/projects/${scope.projectId}/custom_views/new?parent_id=${parentId}&view_type=${scope.module}`,
    );
  };

  const handleNodeExpand = async (node: ITreeItem) => {
    if (node?.children?.length) return;
    try {
      node.extra.isLoading = true;
      node.extra.error = null;
      const filter = node.id.includes("/")
        ? await FiltersService.fetchAutoFilterChildren(scope, node)
        : await FiltersService.fetchFilterFromServer(scope, node.id);

      node.children = [...filter.children];
      node.extra.isLoading = false;
      setItems([...items]);
    } catch (e) {
      console.error(`FolderSearchableTree=>handleNodeExpand`, e);
      node.extra.error = GENERIC_ERROR_MESSAGE;
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      node.extra.isLoading = false;
    }
  };

  const handleServerSideSearch = async (
    criteria: string,
  ): Promise<ISearchResult> => {
    return await SearchService.serverSearch(scope, criteria);
  };

  return (
    <FiltersContext.Provider
      value={{
        items,
        selectedItem,
        loading,
        error,
        settings,
        scope,
        criteria,
        setItems,
        setLoading,
        addSubFilter,
        deleteFilter,
        editView,
        createView,
        copyAsPtql,
        moveView,
        getDeletionPreventionStatus,
        setSelectedItem,
        fetchFilters,
        reloadItems,
        setCriteria,
        notifyFilterChanged,
        handleNodeExpand,
        handleServerSideSearch,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};
